/**
 * AppICEMAP — Polling Scheduler
 * 30-second polling for report data. Pauses when offline, resumes on reconnect.
 *
 * TWO areas are polled and merged:
 *   • the map viewport  → what the markers need
 *   • a circle around the user → what the nearby list needs
 *
 * They are separate on purpose. With only the viewport query, panning the map
 * away emptied the list of nearby reports, even though those reports were still
 * next to the user: the list has to work off the radius setting, not off where
 * the map happens to be looking.
 */

import { fetchReportsInBounds } from "./reports.js";
import { dispatch } from "../store/index.js";
import { writeCache, CACHE_KEYS } from "../services/cache.js";

/** @type {number | null} */
let intervalId = null;

/** @type {{ sw: [number, number], ne: [number, number] } | null} */
let currentBounds = null;

/** @type {string[]} */
let currentCategoryFilters = [];

/** @type {{ center: [number, number], radiusKm: number } | null} */
let nearbyArea = null;

/**
 * Anchor for the nearby query, kept STICKY on purpose.
 *
 * It used to be wiped whenever updateNearby() was called without a position —
 * which happens on any filter change before the first GPS fix — and, since a
 * stationary user never publishes a new one, the nearby query was then gone for
 * the rest of the session and the list silently fell back to following the map.
 *
 * @type {[number, number] | null}
 */
let lastCenter = null;

/** @type {boolean} true when the anchor came from a real GPS fix */
let anchorFromGps = false;

/** localStorage key for the last known anchor */
const CENTER_KEY = "appicemap-last-center";

function loadLastCenter() {
	try {
		const raw = localStorage.getItem(CENTER_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) && parsed.length === 2 ? parsed : null;
	} catch {
		return null;
	}
}

function saveLastCenter(center) {
	try {
		localStorage.setItem(CENTER_KEY, JSON.stringify(center));
	} catch {
		/* private mode */
	}
}

// Hidratar el ancla recordada. Va AQUÍ, después de declarar CENTER_KEY: si se
// hace en la propia declaración de `lastCenter`, la constante está en la zona
// muerta temporal, loadLastCenter() lanza ReferenceError y el try/catch lo
// devuelve como "no hay nada guardado".
lastCenter = loadLastCenter();
anchorFromGps = lastCenter !== null;

/**
 * Is the nearby query anchored on the user (as opposed to the map centre)?
 * The caller uses this to decide whether panning the map may move the anchor.
 */
export function hasGpsAnchor() {
	return anchorFromGps;
}

/**
 * Radius used for the nearby query when the user picked "sin límite". Big
 * enough to cover a metro area without asking the server for a whole state.
 */
const DEFAULT_NEARBY_RADIUS_KM = 50;

/** Polling interval in milliseconds */
const POLL_INTERVAL = 30_000; // 30 seconds

/** Whether polling is currently active */
let running = false;

/** @type {number | null} debounce timer for bounds-triggered polls */
let boundsPollTimer = null;

/**
 * Start the polling scheduler.
 */
export function start() {
	if (running) return;
	running = true;

	// Setup online/offline listeners
	window.addEventListener("online", onOnline);
	window.addEventListener("offline", onOffline);

	// Start interval if online
	if (navigator.onLine) {
		schedulePoll();
	}
}

/**
 * Stop the polling scheduler.
 */
export function stop() {
	running = false;
	clear();

	window.removeEventListener("online", onOnline);
	window.removeEventListener("offline", onOffline);
}

/**
 * Update the viewport bounds for polling.
 * @param {{ sw: [number, number], ne: [number, number] }} bounds
 * @param {string[]} [categories]
 */
export function updateBounds(bounds, categories = []) {
	currentBounds = bounds;
	currentCategoryFilters = categories;

	// Fetch promptly whenever the viewport/filters change instead of waiting for
	// the next 30s tick — otherwise markers lag (or seem to "disappear" on
	// reload until the interval fires). Debounced so rapid pans coalesce.
	if (running && navigator.onLine) {
		if (boundsPollTimer) clearTimeout(boundsPollTimer);
		boundsPollTimer = setTimeout(() => poll(), 300);
	}
}

/**
 * Update the area polled for the nearby list.
 * @param {[number, number] | null} center - [lat, lng] of the user
 * @param {number | null} radiusKm - null uses DEFAULT_NEARBY_RADIUS_KM
 */
export function updateNearby(center, radiusKm, { fromGps = false } = {}) {
	const valid = Array.isArray(center) && center.length === 2;

	if (valid && fromGps) {
		// A real position always wins and is remembered for the next cold start
		lastCenter = center;
		anchorFromGps = true;
		saveLastCenter(center);
	} else if (valid && !anchorFromGps) {
		// Fallback anchor (map centre) — used only while we have no position,
		// and never persisted: it is not where the user lives.
		lastCenter = center;
	}

	// No anchor yet (no GPS ever, first run): nothing to centre on
	if (!lastCenter) {
		nearbyArea = null;
		return;
	}

	const radius = radiusKm || DEFAULT_NEARBY_RADIUS_KM;
	const changed =
		!nearbyArea ||
		nearbyArea.radiusKm !== radius ||
		nearbyArea.center[0] !== lastCenter[0] ||
		nearbyArea.center[1] !== lastCenter[1];

	nearbyArea = { center: lastCenter, radiusKm: radius };

	if (changed && running && navigator.onLine) {
		if (boundsPollTimer) clearTimeout(boundsPollTimer);
		boundsPollTimer = setTimeout(() => poll(), 300);
	}
}

/**
 * Bounding box covering a circle of `radiusKm` around a point.
 * @param {[number, number]} center - [lat, lng]
 * @param {number} radiusKm
 * @returns {{ sw: [number, number], ne: [number, number] }}
 */
function boundsAround(center, radiusKm) {
	const [lat, lng] = center;
	const dLat = radiusKm / 111;
	// Meridians converge toward the poles, so a degree of longitude is shorter
	const dLng = radiusKm / (111 * Math.max(0.1, Math.cos((lat * Math.PI) / 180)));

	return {
		sw: [lat - dLat, lng - dLng],
		ne: [lat + dLat, lng + dLng],
	};
}

/**
 * Force an immediate refresh.
 */
export async function forceRefresh() {
	if (!currentBounds && !nearbyArea) return;
	await poll();
}

/* ── Internal ──────────────────────────────────────────────────────────── */

function schedulePoll() {
	clear();
	intervalId = setInterval(poll, POLL_INTERVAL);
	// Also run first poll immediately
	poll();
}

function clear() {
	if (intervalId !== null) {
		clearInterval(intervalId);
		intervalId = null;
	}
}

async function poll() {
	if (!navigator.onLine) return;
	if (!currentBounds && !nearbyArea) return;

	const options = {
		categories:
			currentCategoryFilters.length > 0 ? currentCategoryFilters : undefined,
	};

	// The two areas usually overlap, so both queries run in parallel and the
	// results are merged by id.
	const areas = [];
	if (currentBounds) areas.push(currentBounds);
	if (nearbyArea) areas.push(boundsAround(nearbyArea.center, nearbyArea.radiusKm));

	try {
		dispatch("SET_UI", { reportsLoading: true });

		const results = await Promise.all(
			areas.map((bounds) =>
				fetchReportsInBounds(bounds, options).catch((err) => {
					// One area failing must not blank out the other
					console.warn("[Poller] Área fallida:", err.message);
					return [];
				}),
			),
		);

		const byId = new Map();
		for (const list of results) {
			for (const report of list) byId.set(report.id, report);
		}

		const merged = [...byId.values()];
		dispatch("SET_REPORTS", merged);

		// Local-first: lo último que se sabe queda guardado para el próximo
		// arranque. No se espera a que termine: no debe frenar el render.
		//
		// Una respuesta VACÍA no se guarda: es lo normal al mirar una zona sin
		// reportes, y machacaría la última copia buena, dejando la próxima
		// apertura en frío otra vez sin nada que mostrar.
		if (merged.length > 0) writeCache(CACHE_KEYS.reports, merged);
	} catch (err) {
		console.warn("[Poller] Poll failed:", err.message);
		dispatch("SET_UI", { reportsLoading: false });
	}
}

function onOnline() {
	dispatch("SET_OFFLINE", false);
	if (running) {
		schedulePoll();
	}
}

function onOffline() {
	dispatch("SET_OFFLINE", true);
	clear();
}
